/**
 * @file: use-thread-tabs.ts
 *
 * Platform-agnostic logic for the inbox two-tab thread container: the active
 * tab, the focus signal that suppresses chat banners while the list is on
 * screen, the unread counts, and opening the contacts panel. The two platform
 * shells (`index.tsx` native pager / `index.web.tsx` conditional render) wrap
 * the same frame around this — only the tab-content swap differs, so everything
 * here lives in one place. The contacts panel itself (and its 1:1 routing) is
 * owned by `InboxOverlayHost`; here we just open it via `overlay$`.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useChatUnreadCount } from '@euler/app/flows/chat/lib/session/hooks/useChatUnreadCount';
import { useFeedUnreadStore } from '@euler/app/flows/feed/lib/unread/context';
import { useFeedUnreadCount } from '@euler/app/flows/feed/lib/unread/hooks/useFeedUnreadCount';
import { StoreFeatureAccess } from '@euler/functions/entitlements/storeFeatureAccess';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { StoreFeature } from '@euler/model/enum';
import { FeedMessageType } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect } from 'react';
import { useInboxContext } from '../../context';
import { InboxTab } from '../../types';

export interface ThreadTabsModel {
  tab: InboxTab;
  /** The chat tab shows only while the store has chat, on trial or not. */
  chat: StoreFeatureAccess;
  onTabChange: (next: InboxTab) => void;
  feedUnread: number;
  chatUnread: number;
  openContacts: () => void;
  /**
   * Mark every feed message in the user's inbox as read. Optimistically
   * zeros the burger badge, calls the API, then bumps the inbox's
   * `feedListReloadTick$` so the feed thread list silently re-fetches
   * (no pull indicator) to land per-row zeroes too.
   */
  markAllFeedRead: () => Promise<void>;
}

export function useThreadTabs(): ThreadTabsModel {
  const { activeTab$, overlay$, feedListReloadTick$ } = useInboxContext();
  const [selected] = useBehaviorSubject(activeTab$);
  const chat = useStoreFeature(StoreFeature.Chat);
  // Without chat the inbox is its notifications alone, whatever asked for
  // the chat tab (a chat push).
  const tab: InboxTab = chat.enabled ? selected : 'notifications';
  const { feedService } = useServiceFactory();
  const { refreshFeedUnread } = useFeedUnreadStore();

  const onTabChange = useCallback(
    (next: InboxTab) => activeTab$.next(next),
    [activeTab$],
  );

  // Signal the chat session while the chat list is the focused surface, so an
  // incoming chat push doesn't pop a banner over a list the user is already
  // looking at. (The conversation screen owns the per-room signal.)
  const isFocused = useIsFocused();
  const { setChatListActive } = useChatSession();
  useEffect(() => {
    setChatListActive(isFocused && tab === 'messages');
    return () => setChatListActive(false);
  }, [isFocused, tab, setChatListActive]);

  const feedUnread = useFeedUnreadCount();
  const chatUnread = useChatUnreadCount();

  const openContacts = useCallback(
    () => overlay$.next({ kind: 'contacts' }),
    [overlay$],
  );

  const markAllFeedRead = useCallback(async () => {
    // Maintenance-plan reminders are actionable items — "全部已读" must
    // not clear them, so they're excluded and stay unread (and keep the
    // global badge non-zero), which is why we don't optimistically zero
    // the count here; refreshFeedUnread sets the true remainder.
    try {
      await feedService.markAllMessagesRead({
        excludeType: FeedMessageType.OrgMaintenancePlanRemind,
      });
    } finally {
      void refreshFeedUnread();
      feedListReloadTick$.next(feedListReloadTick$.getValue() + 1);
    }
  }, [feedService, refreshFeedUnread, feedListReloadTick$]);

  return {
    tab,
    chat,
    onTabChange,
    feedUnread,
    chatUnread,
    openContacts,
    markAllFeedRead,
  };
}
